import React from 'react';
import { bool, string } from 'prop-types';
import _ from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Input, Select, Text } from 'theme-ui';
import { Stack } from '@bedrock-layout/stack';
import { Inline } from '@bedrock-layout/inline';
import { Trash2Outline } from '@styled-icons/evaicons-outline/Trash2Outline';
import { PlusCircle } from '@styled-icons/evaicons-solid/PlusCircle';
import SwitchField from 'Components/molecules/SwitchField';

const TYPE_WIDTH = 130;
const ROW_GAP = 16;
// Name and value share the flexible space equally (each 1fr), the type dropdown keeps
// a fixed min width, and the delete icon takes only its own width.
const ROW_COLUMNS = `minmax(0, 1fr) ${TYPE_WIDTH}px minmax(0, 1fr) auto`;

/**
 * Renders a dynamic, user-editable list of code-flag rows backed by a
 * react-hook-form `useFieldArray`. Each row is
 * `{ key, type, boolValue, stringValue }` where `key` is a free-text name and
 * `type` (`'boolean' | 'string'`) selects the value editor: a SwitchField toggle
 * for booleans or a text Input for strings. Rows can be added and removed; when
 * `disabled` the names render as read-only labels and the add/delete controls are
 * hidden.
 *
 * The array is transformed to/from an object map when the form is loaded/saved
 * (see Utils/codeFlags) since the backend stores a `{ name: boolean | string }` map.
 */
function KeyValueFieldArray({ name, disabled = false, addLabel = 'Add flag', keyPlaceholder = 'Flag name' }) {
  const { control, register, watch, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name });

  return (
    <Stack gutter="md">
      {fields.length === 0 && (
        <Text variant="label" sx={{ color: 'disabledText', fontStyle: 'italic' }}>
          {disabled ? 'No code flags.' : 'No code flags yet — add one below.'}
        </Text>
      )}

      {fields.map((field, index) => {
        const keyName = `${name}.${index}.key`;
        const typeName = `${name}.${index}.type`;
        const boolValueName = `${name}.${index}.boolValue`;
        const stringValueName = `${name}.${index}.stringValue`;
        const error = _.get(formState.errors, keyName);
        const rowType = watch(typeName) ?? field.type;

        return (
          <Box key={field.id}>
            <Box
              sx={{
                display: 'grid',
                // Stack into a single column on small screens; use the full row layout otherwise.
                gridTemplateColumns: ['1fr', ROW_COLUMNS],
                gap: `${ROW_GAP}px`,
                alignItems: 'center',
              }}
            >
              <Input
                defaultValue={field.key}
                placeholder={keyPlaceholder}
                variant={disabled ? 'input.disabled' : error ? 'input.error' : 'input'}
                sx={{ width: '100%' }}
                disabled={disabled}
                {...register(keyName)}
              />
              <Select
                defaultValue={field.type}
                sx={{ width: '100%', height: 46, borderColor: 'darkGreyOutline' }}
                disabled={disabled}
                {...register(typeName)}
              >
                <option value="boolean">Boolean</option>
                <option value="string">String</option>
              </Select>
              {rowType === 'string' ? (
                <Input
                  defaultValue={field.stringValue}
                  placeholder="Value"
                  variant={disabled ? 'input.disabled' : 'input'}
                  sx={{ width: '100%' }}
                  disabled={disabled}
                  {...register(stringValueName)}
                />
              ) : (
                <Box sx={{ justifySelf: 'start' }}>
                  <SwitchField label="" name={boolValueName} disabled={disabled} />
                </Box>
              )}
              {!disabled && (
                <Box
                  as="button"
                  type="button"
                  aria-label={`Delete ${field.key || 'flag'}`}
                  onClick={() => remove(index)}
                  sx={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'blueGrey',
                    transition: 'color 0.15s ease',
                    ':hover': { color: 'errorText' },
                  }}
                >
                  <Trash2Outline width={22} />
                </Box>
              )}
            </Box>
            {error && (
              <Text color="errorText" sx={{ mt: 1, fontSize: 1 }}>
                {error.message}
              </Text>
            )}
          </Box>
        );
      })}

      {!disabled && (
        <Box
          as="button"
          type="button"
          onClick={() => append({ key: '', type: 'boolean', boolValue: false, stringValue: '' })}
          sx={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            p: 0,
            mt: 2,
            color: 'primary',
            width: 'fit-content',
            ':hover .add-flag-label': { textDecoration: 'underline' },
          }}
        >
          <Inline gutter="sm" align="center">
            <PlusCircle width={22} />
            <Text variant="label" className="add-flag-label" sx={{ color: 'primary' }}>
              {addLabel}
            </Text>
          </Inline>
        </Box>
      )}
    </Stack>
  );
}

KeyValueFieldArray.propTypes = {
  name: string.isRequired,
  disabled: bool,
  addLabel: string,
  keyPlaceholder: string,
};

export default KeyValueFieldArray;
